import type {
  EmailTemplate,
  ReceivedEmail,
  SentEmail,
} from "@/domains/communication/api/types";

export const SEED_TEMPLATES: ReadonlyArray<EmailTemplate> = [
  {
    id: "tpl-request-confirmed",
    name: "Aanvraag bevestigd",
    audience: "requester",
    subject: "Je aanvraag {{requestNumber}} is goedgekeurd",
    body:
      "Beste {{requesterFirstName}},\n\n" +
      "Je aanvraag '{{requestTitle}}' (nummer {{requestNumber}}) voor cameratoezicht op {{address}} is goedgekeurd.\n" +
      "De geplande inzet loopt van {{startDate}} tot {{endDate}}.\n\n" +
      "Je wordt op de hoogte gehouden van de plaatsing.\n\n" +
      "Met vriendelijke groeten,\nTeam functionele ondersteuning\nPolitiezone Antwerpen",
    variables: [
      "requesterFirstName",
      "requestNumber",
      "requestTitle",
      "address",
      "startDate",
      "endDate",
    ],
  },
  {
    id: "tpl-placement-scheduled",
    name: "Plaatsing gepland — naar plaatsingsfirma",
    audience: "partner",
    subject: "Plaatsingsopdracht — dossier #{{caseNumber}} {{caseTitle}}",
    body:
      "Beste,\n\n" +
      "Hieronder de details voor de plaatsing van dossier #{{caseNumber}} '{{caseTitle}}':\n\n" +
      "Locatie: {{address}}\n" +
      "Periode: {{startDate}} → {{endDate}}\n" +
      "Assets: {{assetSummary}}\n\n" +
      "Kijkrichting en plaatsingsdetails zie bijlage of in dossier op het platform.\n\n" +
      "Bevestig de geplande plaatsdatum graag via reply.\n\n" +
      "Met vriendelijke groeten,\nTeam functionele ondersteuning\nPolitiezone Antwerpen",
    variables: [
      "caseNumber",
      "caseTitle",
      "address",
      "startDate",
      "endDate",
      "assetSummary",
    ],
  },
  {
    id: "tpl-reminder-partner",
    name: "Herinnering partner",
    audience: "partner",
    subject: "Herinnering: openstaande taken dossier #{{caseNumber}}",
    body:
      "Beste,\n\n" +
      "Een vriendelijke herinnering aan de nog openstaande taken op dossier #{{caseNumber}} '{{caseTitle}}'.\n" +
      "Graag een update bezorgen voor {{deadline}}.\n\n" +
      "Met vriendelijke groeten,\nTeam functionele ondersteuning\nPolitiezone Antwerpen",
    variables: ["caseNumber", "caseTitle", "deadline"],
  },
  {
    id: "tpl-case-closed",
    name: "Dossier afgesloten",
    audience: "requester",
    subject: "Dossier {{caseNumber}} is afgesloten",
    body:
      "Beste {{requesterFirstName}},\n\n" +
      "Het cameratoezicht op '{{address}}' (dossier #{{caseNumber}}) werd op {{endDate}} beëindigd.\n" +
      "Het dossier is nu afgesloten. Alle beelden en documenten zijn gearchiveerd volgens de geldende GDPR-regels.\n\n" +
      "Met vriendelijke groeten,\nTeam functionele ondersteuning\nPolitiezone Antwerpen",
    variables: ["requesterFirstName", "caseNumber", "address", "endDate"],
  },
];

export const SEED_SENT_EMAILS: ReadonlyArray<SentEmail> = [
  {
    id: "em-9214-001",
    caseId: "case-9214",
    templateId: "tpl-request-confirmed",
    subject: "Je aanvraag AANV-2026-9214 is goedgekeurd",
    body:
      "Beste Jan,\n\nJe aanvraag 'Overlast De Coninckplein' (nummer AANV-2026-9214) voor cameratoezicht op De Coninckplein 1, 2060 Antwerpen is goedgekeurd. De geplande inzet loopt van 2026-05-05 tot 2026-05-19.\n\nJe wordt op de hoogte gehouden van de plaatsing.\n\nMet vriendelijke groeten,\nTeam functionele ondersteuning\nPolitiezone Antwerpen",
    to: [{ email: "maarten.hendrickx@police.belgium.eu", name: "Maarten Hendrickx" }],
    sentAt: "2026-04-29T10:08:00",
    sentByName: "Lien Vandevelde",
  },
  {
    id: "em-9214-002",
    caseId: "case-9214",
    templateId: "tpl-placement-scheduled",
    subject: "Plaatsingsopdracht — dossier #9214 Overlast De Coninckplein",
    body:
      "Beste,\n\nHieronder de details voor de plaatsing van dossier #9214 'Overlast De Coninckplein':\n\nLocatie: De Coninckplein 1, 2060 Antwerpen\nPeriode: 2026-05-05 → 2026-05-19\nAssets: MC-001 (camera) + BP-001 (batterij pack)\n\nKijkrichting en plaatsingsdetails zie bijlage of in dossier op het platform.\n\nBevestig de geplande plaatsdatum graag via reply.\n\nMet vriendelijke groeten,\nTeam functionele ondersteuning\nPolitiezone Antwerpen",
    to: [{ email: "jeroen@vermeer-camerasystemen.be", name: "Jeroen Vermeer" }],
    sentAt: "2026-04-30T14:30:00",
    sentByName: "Lien Vandevelde",
  },
];

export const SEED_RECEIVED_EMAILS: ReadonlyArray<ReceivedEmail> = [
  {
    id: "rcv-9214-001",
    caseId: "case-9214",
    inReplyToId: "em-9214-001",
    subject: "Re: Je aanvraag AANV-2026-9214 is goedgekeurd",
    body:
      "Bedankt voor de bevestiging!\n\nKan ik op de hoogte gehouden worden zodra de plaatsing definitief gepland is?\n\nMvg,\nMaarten Hendrickx",
    from: { email: "maarten.hendrickx@police.belgium.eu", name: "Maarten Hendrickx" },
    receivedAt: "2026-04-29T11:42:00",
  },
  {
    id: "rcv-9214-002",
    caseId: "case-9214",
    inReplyToId: "em-9214-002",
    subject: "Re: Plaatsingsopdracht — dossier #9214 Overlast De Coninckplein",
    body:
      "Beste Lien,\n\nDe plaatsing is bevestigd voor 05/05 in de voormiddag (ca. 09u30). Onze technieker Jeroen rijdt ter plaatse. Ik hou jullie op de hoogte.\n\nMvg,\nJeroen Vermeer\nVermeer Mobiele Camerasystemen",
    from: { email: "jeroen@vermeer-camerasystemen.be", name: "Jeroen Vermeer" },
    receivedAt: "2026-05-01T08:15:00",
  },
];
